<template>
    <div>
        <div class="card shadow bg-white position-share-card">
            <div class="card-header d-flex align-items-center justify-content-between position-share-header">
                <div class="min-width-0">
                    <div class="position-share-title">
                        Lien partage de position
                    </div>

                    <div class="position-share-subtitle">
                        {{ activeLinks.length }}
                        {{ activeLinks.length > 1 ? 'liens actifs' : 'lien actif' }}
                    </div>
                </div>

                <div class="d-flex align-items-center flex-shrink-0 position-share-create">
                    <select v-model.number="durationMinutes" class="form-select form-select-sm position-share-duration"
                        :disabled="isCreating" aria-label="Durée du partage">

                        <option :value="30">30 min</option>
                        <option :value="60">1 h</option>
                        <option :value="120">2 h</option>
                        <option :value="180">3 h</option>
                        <option :value="360">6 h</option>
                        <option :value="720">12 h</option>
                        <option :value="1440">24 h</option>
                    </select>

                    <button type="button" class="btn btn-sm btn-primary position-share-create-button"
                        :disabled="isCreating || !balise.id" title="Créer un lien de partage"
                        aria-label="Créer un lien de partage" @click="createLink">

                        <span v-if="isCreating" class="spinner-border spinner-border-sm" role="status"
                            aria-hidden="true" />

                        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                        </svg>

                        <span class="position-share-button-label">
                            Créer
                        </span>
                    </button>
                </div>
            </div>

            <div class="card-body p-0">
                <div v-if="message" :class="[
                    'position-share-message',
                    messageType === 'error'
                        ? 'position-share-message-error'
                        : 'position-share-message-success'
                ]">
                    {{ message }}
                </div>

                <div v-if="isLoading && links.length === 0" class="position-share-empty">

                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />

                    Chargement...
                </div>

                <div v-else-if="activeLinks.length === 0" class="position-share-empty">
                    Aucun partage actif
                </div>

                <div v-else class="list-group list-group-flush">

                    <div v-for="shareLink in activeLinks" :key="shareLink.id"
                        class="list-group-item position-share-row">

                        <div class="d-flex align-items-center w-100 min-width-0">
                            <div class="position-share-status flex-shrink-0">
                                <span class="position-share-dot" />
                            </div>

                            <div class="position-share-main min-width-0">
                                <div class="position-share-primary-line">
                                    <span class="position-share-link-title">
                                        Lien #{{ shareLink.id }}
                                    </span>

                                    <span class="position-share-separator">
                                        ·
                                    </span>

                                    <span class="position-share-remaining">
                                        {{ getRemainingTime(shareLink.expiresAt) }}
                                    </span>
                                </div>

                                <div class="position-share-secondary-line">
                                    Expire le {{ formatDate(shareLink.expiresAt) }}
                                </div>
                            </div>

                            <div class="d-flex align-items-center flex-shrink-0 position-share-actions">
                                <button type="button" class="btn btn-sm position-share-action" :class="{
                                    'position-share-action-copied':
                                        copiedLinkId === shareLink.id
                                }" :disabled="!shareLink.token" :title="copiedLinkId === shareLink.id
                                    ? 'Lien copié'
                                    : 'Copier le lien'" :aria-label="copiedLinkId === shareLink.id
                                            ? 'Lien copié'
                                            : 'Copier le lien'" @click="copyLink(shareLink)">

                                    <svg v-if="copiedLinkId !== shareLink.id" viewBox="0 0 24 24" aria-hidden="true">
                                        <rect x="9" y="9" width="11" height="11" rx="2" />

                                        <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
                                    </svg>

                                    <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="m5 12 4 4L19 6" />
                                    </svg>

                                    <span class="position-share-button-label">
                                        {{ copiedLinkId === shareLink.id ? 'Copié' : 'Copier' }}
                                    </span>
                                </button>

                                <button type="button"
                                    class="btn btn-sm position-share-action position-share-action-danger"
                                    :disabled="revokingLinkId === shareLink.id" title="Révoquer le lien"
                                    aria-label="Révoquer le lien" @click="revokeLink(shareLink)">

                                    <span v-if="revokingLinkId === shareLink.id"
                                        class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />

                                    <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M3 6h18" />
                                        <path d="M8 6V4h8v2" />
                                        <path d="M19 6l-1 14H6L5 6" />
                                        <path d="M10 11v5" />
                                        <path d="M14 11v5" />
                                    </svg>

                                    <span class="position-share-button-label">
                                        Révoquer
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-footer position-share-footer">
                <span>
                    Les liens donnent temporairement accès à la dernière position connue.
                </span>

                <router-link v-if="balise.id" class="position-share-footer-link" :to="{
                    name: 'PositionShareLinks',
                    params: {
                        id: balise.id
                    }
                }">
                    Gérer les liens

                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="m13 6 6 6-6 6" />
                    </svg>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import PositionShareService from '@/services/positionShare.service'

const PUBLIC_POSITION_SHARE_BASE_URL =
    'https://espace.argos-geoloc.com/positionshare';


export default {
    name: 'PositionShareCard',

    props: {
        balise: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            links: [],
            durationMinutes: 60,
            isLoading: false,
            isCreating: false,
            revokingLinkId: null,
            copiedLinkId: null,
            message: '',
            messageType: 'success',
            now: Date.now(),
            remainingTimeInterval: null,
            copiedMessageTimeout: null,
            messageTimeout: null
        };
    },

    computed: {
        activeLinks() {
            return this.links.filter(shareLink => {
                const expiresAt = new Date(
                    shareLink.expiresAt
                ).getTime();

                return (
                    !shareLink.revokedAt &&
                    expiresAt > this.now
                );
            });
        }
    },

    watch: {
        'balise.id': {
            immediate: true,

            handler(baliseId) {
                if (!baliseId) {
                    this.links = [];
                    return;
                }

                this.loadLinks();
            }
        }
    },

    mounted() {
        this.remainingTimeInterval = setInterval(() => {
            this.now = Date.now();
        }, 1000);
    },

    beforeUnmount() {
        if (this.remainingTimeInterval) {
            clearInterval(this.remainingTimeInterval);
        }

        if (this.copiedMessageTimeout) {
            clearTimeout(this.copiedMessageTimeout);
        }

        if (this.messageTimeout) {
            clearTimeout(this.messageTimeout);
        }
    },

    methods: {
        loadLinks() {
            if (!this.balise.id) {
                return Promise.resolve();
            }

            this.isLoading = true;

            return PositionShareService
                .getPositionShareLinks(this.balise.id)
                .then(response => {
                    this.links = Array.isArray(response.data)
                        ? response.data
                        : [];
                })
                .catch(error => {
                    console.error(
                        'Erreur récupération des liens de partage :',
                        error
                    );

                    this.showMessage(
                        error.response?.data?.message ||
                        'Impossible de récupérer les liens.',
                        'error'
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        createLink() {
            if (
                !this.balise.id ||
                this.isCreating
            ) {
                return;
            }

            this.isCreating = true;

            PositionShareService
                .createPositionShareLink(
                    this.balise.id,
                    this.durationMinutes
                )
                .then(response => {
                    const createdLink = response.data;

                    return this.loadLinks()
                        .then(() => {
                            const publicUrl =
                                this.getPublicShareUrl(createdLink);

                            if (!publicUrl) {
                                this.showMessage(
                                    'Lien créé.',
                                    'success'
                                );

                                return null;
                            }

                            return this.copyText(publicUrl)
                                .then(() => {
                                    this.copiedLinkId = createdLink.id;

                                    this.showMessage(
                                        'Lien créé et copié.',
                                        'success'
                                    );

                                    this.resetCopiedState();
                                })
                                .catch(() => {
                                    this.showMessage(
                                        'Lien créé.',
                                        'success'
                                    );
                                });
                        });
                })
                .catch(error => {
                    console.error(
                        'Erreur création du lien de partage :',
                        error
                    );

                    this.showMessage(
                        error.response?.data?.message ||
                        'Impossible de créer le lien.',
                        'error'
                    );
                })
                .finally(() => {
                    this.isCreating = false;
                });
        },

        revokeLink(shareLink) {
            if (
                !this.balise.id ||
                !shareLink.id ||
                this.revokingLinkId !== null
            ) {
                return;
            }

            const confirmed = window.confirm(
                'Révoquer ce lien de partage ?'
            );

            if (!confirmed) {
                return;
            }

            this.revokingLinkId = shareLink.id;

            PositionShareService
                .revokePositionShareLink(
                    this.balise.id,
                    shareLink.id
                )
                .then(() => {
                    this.links = this.links.map(link => {
                        if (link.id !== shareLink.id) {
                            return link;
                        }

                        return {
                            ...link,
                            revokedAt: new Date().toISOString(),
                            isRevoked: true,
                            isActive: false
                        };
                    });

                    this.showMessage(
                        'Lien révoqué.',
                        'success'
                    );
                })
                .catch(error => {
                    console.error(
                        'Erreur révocation du lien de partage :',
                        error
                    );

                    this.showMessage(
                        error.response?.data?.message ||
                        'Impossible de révoquer le lien.',
                        'error'
                    );
                })
                .finally(() => {
                    this.revokingLinkId = null;
                });
        },

        getPublicShareUrl(shareLink) {
            if (!shareLink?.token) {
                return null;
            }

            return `${PUBLIC_POSITION_SHARE_BASE_URL}/${encodeURIComponent(
                shareLink.token
            )}`;
        },

        copyLink(shareLink) {
            const publicUrl =
                this.getPublicShareUrl(shareLink);

            if (!publicUrl) {
                return;
            }

            this.copyText(publicUrl)
                .then(() => {
                    this.copiedLinkId = shareLink.id;

                    this.showMessage(
                        'Lien copié.',
                        'success'
                    );

                    this.resetCopiedState();
                })
                .catch(error => {
                    console.error(
                        'Erreur copie du lien :',
                        error
                    );

                    this.showMessage(
                        'La copie du lien a échoué.',
                        'error'
                    );
                });
        },

        copyText(text) {
            if (
                navigator.clipboard &&
                window.isSecureContext
            ) {
                return navigator.clipboard.writeText(text);
            }

            return new Promise((resolve, reject) => {
                const textarea = document.createElement(
                    'textarea'
                );

                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';

                document.body.appendChild(textarea);

                textarea.select();
                textarea.setSelectionRange(
                    0,
                    textarea.value.length
                );

                try {
                    const copied = document.execCommand('copy');

                    document.body.removeChild(textarea);

                    if (!copied) {
                        reject(new Error('Copie refusée'));
                        return;
                    }

                    resolve();
                } catch (error) {
                    document.body.removeChild(textarea);
                    reject(error);
                }
            });
        },

        showMessage(message, type) {
            this.message = message;
            this.messageType = type;

            if (this.messageTimeout) {
                clearTimeout(this.messageTimeout);
            }

            this.messageTimeout = setTimeout(() => {
                this.message = '';
            }, 2500);
        },

        resetCopiedState() {
            if (this.copiedMessageTimeout) {
                clearTimeout(this.copiedMessageTimeout);
            }

            this.copiedMessageTimeout = setTimeout(() => {
                this.copiedLinkId = null;
            }, 1800);
        },

        formatDate(date) {
            const parsedDate = new Date(date);

            if (Number.isNaN(parsedDate.getTime())) {
                return 'date inconnue';
            }

            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(parsedDate);
        },

        getRemainingTime(expiresAt) {
            const expirationTime = new Date(
                expiresAt
            ).getTime();

            const remainingMilliseconds =
                expirationTime - this.now;

            if (
                Number.isNaN(expirationTime) ||
                remainingMilliseconds <= 0
            ) {
                return 'Expiré';
            }

            const totalSeconds = Math.floor(
                remainingMilliseconds / 1000
            );

            const days = Math.floor(
                totalSeconds / 86400
            );

            const hours = Math.floor(
                (totalSeconds % 86400) / 3600
            );

            const minutes = Math.floor(
                (totalSeconds % 3600) / 60
            );

            if (days > 0) {
                return `${days} j ${hours} h restantes`;
            }

            if (hours > 0) {
                return `${hours} h ${minutes} min restantes`;
            }

            if (minutes > 0) {
                return `${minutes} min restantes`;
            }

            return 'Moins d’une minute';
        }
    }
}
</script>

<style scoped>
.position-share-card {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.position-share-header {
    gap: 0.75rem;
    min-width: 0;
    padding: 0.75rem 1rem;
}

.min-width-0 {
    min-width: 0;
}

.position-share-title {
    overflow: hidden;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-subtitle {
    margin-top: 0.1rem;
    color: #6c757d;
    font-size: 0.72rem;
    line-height: 1.2;
}

.position-share-create {
    gap: 0.35rem;
}

.position-share-duration {
    width: 78px;
    min-width: 78px;
    padding-right: 1.6rem;
    font-size: 0.75rem;
}

.position-share-create-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    height: 31px;
    padding: 0 0.55rem;
    white-space: nowrap;
}

.position-share-create-button svg {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
}

.position-share-message {
    margin: 0.6rem 0.75rem 0;
    padding: 0.45rem 0.65rem;
    font-size: 0.74rem;
    border-radius: 0.375rem;
}

.position-share-message-success {
    color: #0f5132;
    background: #d1e7dd;
}

.position-share-message-error {
    color: #842029;
    background: #f8d7da;
}

.position-share-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 58px;
    padding: 0.85rem 1rem;
    color: #6c757d;
    font-size: 0.76rem;
    text-align: center;
}

.position-share-row {
    padding: 0.55rem 0.75rem;
    background: transparent;
}

.position-share-row:hover {
    background: rgba(0, 0, 0, 0.015);
}

.position-share-status {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    margin-right: 0.45rem;
}

.position-share-dot {
    width: 7px;
    height: 7px;
    background: #198754;
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.12);
}

.position-share-main {
    flex: 1 1 auto;
    overflow: hidden;
}

.position-share-primary-line {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 0.8rem;
    line-height: 1.2;
}

.position-share-link-title {
    flex-shrink: 0;
    font-weight: 600;
    white-space: nowrap;
}

.position-share-separator {
    flex-shrink: 0;
    margin: 0 0.35rem;
    color: #adb5bd;
}

.position-share-remaining {
    overflow: hidden;
    color: #495057;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-secondary-line {
    overflow: hidden;
    margin-top: 0.15rem;
    color: #6c757d;
    font-size: 0.68rem;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.position-share-actions {
    gap: 0.3rem;
    margin-left: 0.6rem;
}

.position-share-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    height: 29px;
    padding: 0 0.4rem;
    color: #6c757d;
    background: transparent;
    border: 0;
    white-space: nowrap;
}

.position-share-button-label {
    font-size: 0.7rem;
    line-height: 1;
}

.position-share-action:hover:not(:disabled) {
    color: #212529;
    background: #e9ecef;
}

.position-share-action-danger:hover:not(:disabled) {
    color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
}

.position-share-action-copied {
    color: #198754;
    background: rgba(25, 135, 84, 0.08);
}

.position-share-action:disabled {
    opacity: 0.45;
}

.position-share-action svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.position-share-action .spinner-border {
    flex-shrink: 0;
    width: 0.8rem;
    height: 0.8rem;
    border-width: 0.12em;
}

.position-share-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.55rem 0.75rem;
    color: #6c757d;
    font-size: 0.68rem;
}

.position-share-footer-link {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: 0.25rem;

    color: #0d6efd;
    font-size: 0.7rem;
    font-weight: 500;
    text-decoration: none;
    white-space: nowrap;
}

.position-share-footer-link:hover {
    color: #0a58ca;
    text-decoration: underline;
}

.position-share-footer-link svg {
    width: 13px;
    height: 13px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
}

@media (max-width: 575.98px) {
    .position-share-header {
        padding: 0.65rem 0.75rem;
    }

    .position-share-title {
        font-size: 0.88rem;
    }

    .position-share-duration {
        width: 70px;
        min-width: 70px;
        padding-right: 1.4rem;
        padding-left: 0.45rem;
    }

    .position-share-create-button {
        padding-right: 0.45rem;
        padding-left: 0.45rem;
    }

    .position-share-row {
        padding-right: 0.5rem;
        padding-left: 0.6rem;
    }

    .position-share-secondary-line {
        display: none;
    }

    .position-share-primary-line {
        display: block;
    }

    .position-share-separator {
        display: none;
    }

    .position-share-remaining {
        display: block;
        margin-top: 0.1rem;
        font-size: 0.72rem;
    }

    .position-share-actions {
        gap: 0.1rem;
        margin-left: 0.2rem;
    }

    .position-share-action {
        padding-right: 0.3rem;
        padding-left: 0.3rem;
    }

    .position-share-button-label {
        font-size: 0.62rem;
    }

    .position-share-footer {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.35rem;
    }
}

@media (max-width: 430px) {
    .position-share-action .position-share-button-label {
        display: none;
    }

    .position-share-action {
        width: 29px;
        padding: 0;
    }
}
</style>